import { useState, useCallback, useMemo, useEffect } from 'react';
import { TargetAndTransition, useAnimation } from 'framer-motion';
import { useDeepCompareMemo } from './useDeepCompareMemo';

export const usePause = (
    initialDefinition: TargetAndTransition,
    duration: number
) => {
    const controls = useAnimation();
    const definition = useDeepCompareMemo(initialDefinition);
    const [lapTimes, setLapTimes] = useState<number[]>([Date.now()]);
    const isPaused = useMemo(() => lapTimes.length % 2 === 0, [lapTimes]);
    const remainingTime = useMemo(() => {
        const totalElapsed = lapTimes.reduce((acc, time, index, arr) => {
            if (index % 2 === 0) {
                return acc + ((arr[index + 1] || Date.now()) - time);
            }
            return acc;
        }, 0);
        return Math.max(duration - totalElapsed, 0);
    }, [lapTimes, duration]);

    const togglePause = useCallback(() => {
        setLapTimes((prev) => [...prev, Date.now()]);
    }, []);

    useEffect(() => {
        if (isPaused) {
            controls.stop();
        } else {
            controls.start({ ...definition, transition: { duration: remainingTime / 1000 } });
        }
    }, [isPaused, controls, definition, remainingTime]);

    return { controls, togglePause, isPaused, remainingTime };
};

